import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import cn from 'classnames';

import TransComp from 'shared/components/TransComp';
import Typography from 'shared/newComponents/Typography';
import {
  TypographyColor,
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { WithMetaProps } from 'shared/newEntities/components/WithMeta';
import CroppedContent from 'shared/newComponents/CroppedContent';
import Reference from 'shared/newComponents/Reference';

import './WithMeta.modules.scss';

type Props = WithMetaProps & {
  children: React.ReactNode;
  className?: string;
};

const WithMeta: React.FC<Props> = ({
  children,
  className,
  error,
  bottomLabel,
  label,
  errorLimited = true,
  errorSize = TypographySize.s,
  labelSize = TypographySize.s,
  labelColor = TypographyColor.secondary,
  reference,
  showErrorText = true,
  isRequired = false,
  errorId
}) => {
  const showError = showErrorText && !!error;

  return (
    <div className={className}>
      {label && (
        <div styleName="meta__label">
          <Typography
            styleName={`meta__label-title_${labelSize}`}
            fontType={TypographyType.text}
            fontSize={labelSize}
            fontWeight={TypographyWeight.heavy}
            fontColor={labelColor}
          >
            {isRequired && '*'}
            <TransComp>{label}</TransComp>
          </Typography>
          {reference && (
            <Reference styleName="meta__label-reference" {...reference} />
          )}
        </div>
      )}
      {children}
      {!showError && bottomLabel && (
        <Typography
          styleName="meta__bottom-label"
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontColor={TypographyColor.secondary}
        >
          {bottomLabel}
        </Typography>
      )}
      <AnimatePresence>
        {showError && (
          <motion.div
            id={errorId}
            style={{ overflowY: 'hidden' }}
            initial={{
              height: 0,
              opacity: 0,
              marginTop: 0
            }}
            animate={{
              height: 'auto',
              opacity: 1,
              marginTop: 6
            }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
          >
            <CroppedContent popupContent={error}>
              <Typography
                fontType={TypographyType.text}
                fontSize={errorSize}
                fontColor={TypographyColor.error}
                styleName={cn(errorLimited && 'meta__error')}
              >
                {error}
              </Typography>
            </CroppedContent>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WithMeta;
